/**
 * Login page component - replaces account/login.html template
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { renderFieldErrors } from "../components/FieldErrorList";
import { SocialAuthButtons } from "../components/SocialAuthButtons";
import {
  useAuthContext,
  useFieldErrors,
  useNonFieldErrors,
} from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

export function LoginPage() {
  const { csrfToken, formData } = useAuthContext();
  const nonFieldErrors = useNonFieldErrors();
  const loginErrors = useFieldErrors("login");
  const passwordErrors = useFieldErrors("password");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(formData?.login ?? formData?.email ?? "");
  const [password, setPassword] = useState("");
  const [showLogoutSnackbar, setShowLogoutSnackbar] = useState(false);

  // Check for logout success parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("logout") === "success") {
      setShowLogoutSnackbar(true);
      // Clean up URL without the parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const handleSubmit = () => {
    setIsSubmitting(true);
    // Let the form submit naturally to Django
  };

  return (
    <AuthLayout>
      {/* Logout Success Snackbar */}
      <Snackbar
        open={showLogoutSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowLogoutSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowLogoutSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {gettext("You have been successfully logged out.")}
        </Alert>
      </Snackbar>

      {/* Social Auth Buttons */}
      <SocialAuthButtons />

      {/* Login Form */}
      <Box
        component="form"
        method="POST"
        action="/accounts/login/"
        onSubmit={handleSubmit}
        sx={{
          padding: "0 2.5rem 2.5rem",
          backgroundColor: "white",
        }}
      >
        <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />

        {/* Non-field errors */}
        {nonFieldErrors.length > 0 && (
          <Alert
            severity="error"
            sx={{ marginBottom: "1rem" }}
            data-testid="login-error"
          >
            {nonFieldErrors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* Email Field */}
        <TextField
          fullWidth
          name="login"
          type="text"
          label={gettext("Email")}
          placeholder={gettext("Enter your email address")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={loginErrors.length > 0}
          helperText={renderFieldErrors(loginErrors)}
          required
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "&:hover fieldset": {
                borderColor: BRAND_COLORS.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: BRAND_COLORS.inputFocus,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
              color: BRAND_COLORS.text.primary,
            },
          }}
          inputProps={{
            "data-testid": "login-email-input",
          }}
        />

        {/* Password Field */}
        <TextField
          fullWidth
          name="password"
          type="password"
          label={gettext("Password")}
          placeholder={gettext("Enter your password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={passwordErrors.length > 0}
          helperText={renderFieldErrors(passwordErrors)}
          required
          sx={{
            marginBottom: "1.5rem",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "&:hover fieldset": {
                borderColor: BRAND_COLORS.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: BRAND_COLORS.inputFocus,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
              color: BRAND_COLORS.text.primary,
            },
          }}
          inputProps={{
            "data-testid": "login-password-input",
          }}
        />

        {/* Submit Button */}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            padding: "0.875rem",
            backgroundColor: BRAND_COLORS.primary,
            borderRadius: 2,
            fontSize: "0.9375rem",
            fontWeight: 600,
            textTransform: "none",
            fontFamily: "'Montserrat', sans-serif",
            marginTop: "0.5rem",
            "&:hover": {
              backgroundColor: BRAND_COLORS.primaryHover,
              boxShadow: 2,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
          data-testid="login-submit-button"
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            gettext("Sign in")
          )}
        </Button>

        {/* Forgot Password Link */}
        <Link
          href="/accounts/password/reset/"
          sx={{
            display: "inline-block",
            marginTop: "1rem",
            color: BRAND_COLORS.link,
            fontSize: "0.875rem",
            textDecoration: "none",
            fontWeight: 500,
            "&:hover": {
              color: BRAND_COLORS.linkHover,
              textDecoration: "underline",
            },
          }}
          data-testid="forgot-password-link"
        >
          {gettext("Forgot your password?")}
        </Link>
      </Box>
    </AuthLayout>
  );
}
